import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router';
import { useCms } from '../../contexts/CmsContext';
import { Target, Users, Award, Shield, ChevronRight, Sparkles } from 'lucide-react';

const iconMap: Record<string, React.ElementType> = { Target, Users, Award, Shield };

const defaultValues = [
  { icon: 'Target', title: 'Practical First', desc: 'Every concept is taught through real-world BFSI projects you can put on your resume.' },
  { icon: 'Shield', title: 'Privacy by Design', desc: 'We teach local AI deployment so your data never leaves your machine — critical for compliance.' },
  { icon: 'Users', title: 'Community Driven', desc: 'Learn alongside finance professionals, not alone. Live cohorts create real accountability.' },
  { icon: 'Award', title: 'Industry Recognized', desc: 'Our certificates are trusted by HDFC, ICICI, Axis, Kotak, and 50+ hiring partners.' },
];

const team = [
  { name: 'Rajesh Kumar', role: 'Founder & Lead Instructor', image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300&h=300&fit=crop&crop=face', bio: 'Ex-Goldman Sachs quant researcher with 15 years in AI & finance. Built AI systems managing ₹500Cr+ AUM.' },
  { name: 'Priya Sharma', role: 'Head of Curriculum', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&crop=face', bio: 'Former FP&A lead at Flipkart. Designed the 12-week curriculum mapping AI skills to real finance workflows.' },
  { name: 'Arun Nair', role: 'Technical Lead', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face', bio: 'Ex-Microsoft AI engineer. Built the local LLM infrastructure that powers our privacy-first approach.' },
];

export default function AboutScreen(): React.JSX.Element {
  const cms = useCms();
  const stats = [
    { num: cms.get('about_stat_1_number', '2,000+'), label: cms.get('about_stat_1_label', 'Students Trained') },
    { num: cms.get('about_stat_2_number', '50+'), label: cms.get('about_stat_2_label', 'BFSI Partners') },
    { num: cms.get('about_stat_3_number', '95%'), label: cms.get('about_stat_3_label', 'Placement Rate') },
    { num: cms.get('about_stat_4_number', '4.9/5'), label: cms.get('about_stat_4_label', 'Average Rating') },
  ];
  const values = cms.getJson('about_values_json', defaultValues);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-6"
          >
            About Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 leading-tight"
          >
            Building the Future of<br />
            <span className="text-amber-500">AI-Powered Finance</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed"
          >
            EcoleMinds is India's first AI training platform built exclusively for finance professionals.
            We transform CAs, bankers, and analysts into AI-powered leaders.
          </motion.p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-4 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <p className="text-3xl sm:text-4xl font-bold text-amber-500">{stat.num}</p>
                <p className="text-sm text-slate-500 mt-1 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-4">{cms.get('about_mission_title', 'Our Mission')}</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                {cms.get('about_mission_p1', 'Finance professionals in India are being left behind by the AI revolution. While tech teams ship AI features in weeks, finance teams spend months in "pilot purgatory" — blocked by compliance, overwhelmed by manual work, and underserved by generic courses.')}
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                {cms.get('about_mission_p2', 'We exist to close that gap. Every course we build is designed for the realities of Indian BFSI: RBI compliance, local data privacy, NSE/BSE data, and the specific workflows of CAs, bankers, and analysts.')}
              </p>
              <Link to="/" className="inline-flex items-center gap-2 text-amber-500 font-semibold hover:underline">
                Explore Our Courses <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-slate-50 rounded-3xl p-8 border border-slate-100"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">{cms.get('site_name', 'EcoleMinds')}</p>
                  <p className="text-xs text-slate-500">Est. 2024</p>
                </div>
              </div>
              <blockquote className="text-lg text-slate-700 italic leading-relaxed">
                "We don't teach AI theory. We teach AI that works inside a bank, an NBFC, or a Big 4 audit firm — on Monday morning."
              </blockquote>
              <p className="text-sm text-slate-500 mt-4">— Rajesh Kumar, Founder</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">What We Stand For</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v: any, i: number) => {
              const Icon = iconMap[v.icon] || Target;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4">
              Our Team
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Meet the Experts</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full object-cover mx-auto mb-4 ring-4 ring-amber-50"
                />
                <h3 className="text-lg font-bold text-slate-900">{member.name}</h3>
                <p className="text-sm text-amber-500 font-medium mb-3">{member.role}</p>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Career?</h2>
          <p className="text-slate-300 mb-8">
            Join 2,000+ finance professionals who have already made the leap to AI-powered roles.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-400 transition-colors"
          >
            Explore Courses <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
